"use client";

import React, { useEffect, useState } from "react";
import { Terminal, Database, Film, Cpu } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const dataBlocks = [{
  id: "01",
  label: "AUTHOR_PROFILE",
  title: "The Architect",
  description: "Tracy Vetter constructs intricate narratives where high-stakes technology meets human vulnerability. Specializing in sci-fi tech thrillers, the focus is on immersive, atmospheric storytelling that blurs the line between fiction and impending reality.",
  icon: Terminal
}, {
  id: "02",
  label: "PRIMARY_ASSET",
  title: "The Poseidon Project",
  description: "A flagship deep-sea narrative exploring the crushing physical pressure of the ocean floor and the darker, unseen depths of encrypted corporate data. A thriller designed to test the limits of human endurance.",
  icon: Database
}, {
  id: "03",
  label: "MEDIA_EXPANSION",
  title: "Visual Translation",
  description: "Expanding the universe beyond the page. The accompanying short film production provides a cinematic lens into The Poseidon Project, supported by exclusive behind-the-scenes access and immersive publicity events.",
  icon: Film
}];
function DataBlockCard({
  block,
  index
}: {
  block: (typeof dataBlocks)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2,
    rootMargin: "0px 0px -50px 0px"
  });
  return <div ref={ref} data-index={index} className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
    transitionDelay: `${index * 150}ms`
  }}>
      <Card className="relative overflow-hidden rounded-sm border-primary/30 bg-card/40 backdrop-blur-md shadow-[0_0_15px_rgba(0,0,0,0.5)] before:absolute before:inset-0 before:z-[-1] before:bg-gradient-to-br before:from-primary/5 before:to-transparent">
        {}
        <div className="absolute right-0 top-0 flex items-center gap-2 border-b border-l border-primary/30 bg-primary/10 px-3 py-1 font-mono text-xs font-medium tracking-wider text-primary">
          <Cpu className="size-3" />
          SECTOR {block.id}
        </div>

        <CardHeader className="pb-2 pt-8">
          <div className="mb-2 inline-flex h-10 w-10 items-center justify-center rounded-sm bg-primary/10 text-primary ring-1 ring-primary/30">
            <block.icon className="size-5" />
          </div>
          <CardTitle className="font-sans text-xl tracking-wide text-foreground">
            {block.title}
          </CardTitle>
          <div className="font-mono text-[10px] text-primary/60 uppercase tracking-widest">
            
          </div>
        </CardHeader>
        <CardContent>
          <CardDescription className="text-base leading-relaxed text-muted-foreground">
            {block.description}
          </CardDescription>
        </CardContent>
      </Card>
    </div>;
}
export default function CoreDirective() {
  const [offsetY, setOffsetY] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      setOffsetY(window.scrollY);
    };
    if (typeof window !== "undefined") {
      window.addEventListener("scroll", handleScroll, {
        passive: true
      });
      return () => window.removeEventListener("scroll", handleScroll);
    }
  }, []);
  return <section id="core-directive" className="dark relative flex min-h-screen w-full items-center justify-center overflow-hidden bg-background py-24 text-foreground">
      {}
      <div className="pointer-events-none absolute inset-[-20%] z-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1602358/img/topographic_ocean_floor.png)] bg-cover bg-center opacity-10 mix-blend-overlay" style={{
      transform: `translateY(${offsetY * 0.05}px)`,
      willChange: "transform"
    }} aria-hidden="true" />

      {}
      <div className="pointer-events-none absolute inset-0 z-0 bg-gradient-to-b from-background via-transparent to-background" />
      <div className="pointer-events-none absolute inset-0 z-0 bg-[radial-gradient(circle_at_center,transparent_0%,rgba(0,0,0,0.8)_100%)]" />

      <div className="relative z-10 w-full max-w-3xl px-6">
        {}
        <div className="mb-16 text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-4 py-1.5 font-mono text-xs tracking-widest text-primary">
            <span className="relative flex size-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75"></span>
              <span className="relative inline-flex size-2 rounded-full bg-primary"></span>
            </span>
            SYSTEM_ONLINE
          </div>

          {}
          <div className="relative mx-auto inline-block">
            <h2 className="absolute -left-[2px] top-[1px] font-sans text-5xl font-bold tracking-tighter text-primary opacity-70 mix-blend-screen sm:text-6xl md:text-7xl" aria-hidden="true">
              TRACY VETTER
            </h2>
            <h2 className="absolute -right-[2px] -top-[1px] font-sans text-5xl font-bold tracking-tighter text-destructive opacity-70 mix-blend-screen sm:text-6xl md:text-7xl" aria-hidden="true">
              TRACY VETTER
            </h2>
            <h2 className="relative font-sans text-5xl font-bold tracking-tighter text-foreground sm:text-6xl md:text-7xl">
              TRACY VETTER
            </h2>
          </div>
          <p className="mt-6 font-mono text-sm tracking-widest text-muted-foreground uppercase">
            Initiating Core Directive Sequence...
          </p>
        </div>

        {}
        <div className="flex flex-col gap-8">
          {dataBlocks.map((block, index) => <DataBlockCard key={block.id} block={block} index={index} />)}
        </div>
      </div>
    </section>;
}